import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Box, ButtonBase, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { startImportWizard } from '@/features/domain/transaction'
import { selectUserConfiguration } from '@/features/domain/user'
import { ImportOrders, Plus } from '@/icons'
import { useImportWizardLink } from '@/routing'
import { useAppDispatch } from '@/store'
import { importWizardSession } from '@/utils'

import {
  sidebarButtonRootSx,
  sidebarChevronSx,
  sidebarMenuAnchorOrigin,
  sidebarMenuSlotProps,
  sidebarMenuTransformOrigin,
} from '../sidebarButtonStyles'

import { useTexts } from './useTexts'

interface Props {
  disabled?: boolean
  testid?: string
  onAddOrder: () => void
}

export function AddOrderButton(props: Props) {
  const { disabled = false, testid, onAddOrder } = props
  const chevronRef = useRef<HTMLSpanElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const texts = useTexts()

  const { planType } = useSelector(selectUserConfiguration)
  const importWizardUrl = useImportWizardLink()
  const dispatch = useAppDispatch()

  const handleToggle = useCallback(() => {
    if (disabled) return
    setAnchorEl(prev => (prev ? null : chevronRef.current))
  }, [disabled])

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleAddOrderFromScratch = useCallback(() => {
    setAnchorEl(null)
    onAddOrder()
  }, [onAddOrder])

  const handleImportOrders = useCallback(async () => {
    setAnchorEl(null)
    if (!['operations', 'simulation'].includes(planType)) {
      throw new Error(
        `It is not possible to import orders when planType = ${planType}. It should not happen`,
      )
    }
    const response = await dispatch(startImportWizard(globalThis.location.href))
    if (startImportWizard.rejected.match(response) || !response.payload) {
      throw new Error('Request failed')
    }
    importWizardSession.initializeImport(response.payload)
    window.location.href = importWizardUrl
  }, [dispatch, planType, importWizardUrl])

  return (
    <>
      <ButtonBase
        onClick={handleToggle}
        disabled={disabled}
        data-trackid={testid}
        data-testid={testid}
        sx={sidebarButtonRootSx}
      >
        <Stack direction="row" alignItems="center" width="100%">
          <Box
            minWidth={20}
            mr={2.25}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={disabled ? 'text.disabled' : 'text.primary'}
          >
            <Plus size={14} />
          </Box>
          <Typography
            variant="body1"
            fontWeight={500}
            color={disabled ? 'text.disabled' : 'text.primary'}
            align="left"
            noWrap
            flex={1}
          >
            {texts.addOrder}
          </Typography>
          {!disabled && (
            <IconButton
              ref={chevronRef}
              size="small"
              component="span"
              aria-expanded={open}
              sx={sidebarChevronSx}
            >
              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
            </IconButton>
          )}
        </Stack>
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={sidebarMenuAnchorOrigin}
        transformOrigin={sidebarMenuTransformOrigin}
        slotProps={sidebarMenuSlotProps}
      >
        <MenuItem
          onClick={handleAddOrderFromScratch}
          data-testid={`${testid}__from-scratch`}
          data-trackid={`${testid}__from-scratch`}
        >
          <Box mr={1.5} display="flex" alignItems="center" color="action.active">
            <Plus size={14} />
          </Box>
          <Typography variant="body1" color="text.primary">
            {texts.addOrderFromScratch}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleImportOrders}
          data-testid={`${testid}__import-orders`}
          data-trackid={`${testid}__import-orders`}
        >
          <Box mr={1.5} display="flex" alignItems="center" color="action.active">
            <ImportOrders size={14} />
          </Box>
          <Typography variant="body1" color="text.primary">
            {texts.importOrders}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  )
}
